import { getApplicationEnvironments } from '../../../shared/api/controllers/applicationEnvironment';
import { addTelephonyDeploymentTelnyx, addTelephonyDeploymentVonage, getTelephonyDeployments } from '../../../shared/api/controllers/telephonyDeployment';
import { defaultTelephonyDeploymentSettings } from '../../../shared/constants/telephonyConstants.tsx/defaultTelephonyDeploymentSettings';
import { PHONE_PLATFORMS } from '../../../shared/constants/phonePlatforms';
import TelephonyDeploymentRequest from '../../../shared/models/telephonyDeployment/TelephonyDeploymentRequest';
import { TelephonyDeploymentResponse } from '../../../shared/models/telephonyDeployment/TelephonyDeploymentResponse';

interface LocationPhoneOptions {
    applicationId: string;
    organizationName: string;
    locationName: string;
    assistantType: string;
    sourceApplicationId?: string;
    onWarning: (warning: string) => void;
}

export const buildLocationPhoneRequest = (
    environmentId: string,
    name: string,
    assistantType: string,
    source?: TelephonyDeploymentResponse
): TelephonyDeploymentRequest => {
    // Deployment identities and numbers belong to the source app, never the new app.
    const { country, autoTransferPreviouslyTransferred, ...defaults } = defaultTelephonyDeploymentSettings;
    let settings: Partial<TelephonyDeploymentRequest> = {};
    if (source) {
        const { id, applicationId, applicationSecret, phoneNumber, environmentId: sourceEnvironmentId,
            name: sourceName, virtualNumber, outboundCallerIdNumber, ...sourceSettings } = source;
        settings = sourceSettings;
    }
    const isDental = assistantType === 'dental-scheduling';
    return {
        ...defaults,
        callRecordingEnabled: true,
        proactiveResponseDelayMS: isDental ? 1900 : defaults.proactiveResponseDelayMS,
        postProactiveResponsePauseDelayMS: isDental ? 500 : defaults.postProactiveResponsePauseDelayMS,
        generativeProactiveResponsePrompt: isDental
            ? '{{constant.generative_proactive_response_dental}}'
            : assistantType === 'ai-ordering' ? '{{constant.proactive_response_prompt}}' : defaults.generativeProactiveResponsePrompt,
        ...settings,
        ttsOptions: source?.ttsOptions?.length ? source.ttsOptions : source?.ttsVoice
            ? [{ ttsProvider: source.ttsProvider || defaults.ttsOptions[0].ttsProvider, ttsVoice: source.ttsVoice }]
            : defaults.ttsOptions,
        environmentId,
        name,
        phoneNumberCountryCode: source?.phoneNumberCountryCode || country,
        phoneNumberStartsWith: '',
        useVirtualNumber: false,
        virtualNumber: null,
        outboundCallerIdNumber: '',
        useForOutboundCalling: false,
    };
};

/** Creates one new inbound number, preserving any EN/ES deployments sharing the source number. */
export const provisionLocationPhone = async (options: LocationPhoneOptions): Promise<string> => {
    const environments = await getApplicationEnvironments(options.applicationId);
    const production = environments.resultType === 'Ok' && environments.data?.find(env => env.name === 'Production');
    if (!production) throw new Error('The new location has no available Production environment.');

    let sources: TelephonyDeploymentResponse[] = [];
    if (options.sourceApplicationId) {
        const [result, sourceEnvironments] = await Promise.all([
            getTelephonyDeployments(options.sourceApplicationId),
            getApplicationEnvironments(options.sourceApplicationId),
        ]);
        if (result.resultType !== 'Ok') throw new Error(`Could not load the clone source phone settings: ${result.errors?.join(', ') || result.resultType}.`);
        const sourceProduction = sourceEnvironments.resultType === 'Ok' && sourceEnvironments.data?.find(env => env.name === 'Production');
        if (!sourceProduction) throw new Error('Could not load the clone source Production environment.');
        const inbound = (result.data || []).filter(deployment => !!deployment.phoneNumber
            && String(deployment.useForOutboundCalling).toLowerCase() !== 'true'
            && deployment.environmentId === sourceProduction.id);
        const primary = inbound.find(deployment => deployment.locale === 'en-US') || inbound[0];
        if (!primary) throw new Error('The clone source has no inbound phone settings to copy. Set up the phone number from the location dashboard.');
        sources = [primary, ...inbound.filter(deployment => deployment !== primary && deployment.phoneNumber === primary.phoneNumber)];
    }

    const requests = (sources.length ? sources : [undefined]).map(source => buildLocationPhoneRequest(
        production.id,
        [options.organizationName, options.locationName, source?.locale || 'en-US'].filter(Boolean).join(' - '),
        options.assistantType,
        source
    ));
    // Validate every platform before provisioning, so unsupported clones do not partially allocate.
    if (requests.some(request => request.platform && ![PHONE_PLATFORMS.TELNYX, PHONE_PLATFORMS.VONAGE].includes(request.platform))) {
        throw new Error('The clone source uses an unsupported phone platform.');
    }
    let phoneNumber: string;
    for (const request of requests) {
        if (phoneNumber) request.phoneNumberStartsWith = phoneNumber;
        if (request.locale === 'es-US') {
            const { setupSpanishInteractionModel } = await import('./spanishLineSetup');
            await setupSpanishInteractionModel(options.applicationId, options.onWarning);
        }
        const create = request.platform === PHONE_PLATFORMS.VONAGE ? addTelephonyDeploymentVonage : addTelephonyDeploymentTelnyx;
        const result = await create(options.applicationId, request);
        if (result.resultType !== 'Ok' || !result.data?.phoneNumber) {
            throw new Error(`${phoneNumber ? `Phone number ${phoneNumber} was created, but an additional language could not be configured. ` : ''}${result.errors?.join(', ') || 'Phone number provisioning failed.'}`);
        }
        phoneNumber = result.data.phoneNumber;
    }
    return phoneNumber;
};

/** Recheck saved deployments before allocating a number, including on retries. */
export const ensureLocationPhone = async (options: LocationPhoneOptions): Promise<TelephonyDeploymentResponse[]> => {
    const loadDeployments = async () => {
        const result = await getTelephonyDeployments(options.applicationId);
        if (result.resultType !== 'Ok') {
            throw new Error(`Could not load phone settings: ${result.errors?.join(', ') || result.resultType}.`);
        }
        return result.data || [];
    };
    const existing = await loadDeployments();
    if (existing.some(deployment => !!deployment.phoneNumber)) return existing;

    const phoneNumber = await provisionLocationPhone(options);
    try {
        const deployments = await loadDeployments();
        if (!deployments.some(deployment => !!deployment.phoneNumber)) {
            throw new Error('The new phone settings are not yet available.');
        }
        return deployments;
    } catch (err) {
        throw new Error(`Phone number ${phoneNumber} was created, but settings could not be loaded to apply the update: ${err.message || err}`);
    }
};
